import { useCallback, useEffect, useRef } from 'react';
import { WeaveCopilotV1 } from '@frontend/api-weave-copilot';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { useNavigate } from '@frontend/weave-router';
import { ChatbotAction, WeaveCopilotSource } from '../types';

const COPILOT_ACTION_COMM_CHANNEL = 'weave-copilot-action-channel';

export const useChatbotAction = ({ source }: { source: WeaveCopilotSource }) => {
  const { navigate: navigateToSettings } = useSettingsNavigate();
  const navigate = useNavigate();
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const handleAction = useCallback(
    (action: ChatbotAction) => {
      // If this is a popout window, notify the parent window to handle the action
      if (source === 'popout-window') {
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage({ type: COPILOT_ACTION_COMM_CHANNEL, action }, window.origin);
            window.opener.focus();
          } catch (error) {
            console.error('Failed to communicate with parent window:', error);
          }
        }
      } else {
        // Handle actions locally (for main window or if popout communication failed)
        switch (action.type) {
          case WeaveCopilotV1.Types.ActionType.ACTION_TYPE_SETTING_LINK:
            if (action.value) {
              navigateToSettings({ to: action.value as SettingsRoutes, params: {} });
            }
            break;
          case WeaveCopilotV1.Types.ActionType.ACTION_TYPE_FEATURE_LINK:
            navigate({ to: action.value });
            break;
          default:
            break;
        }
      }
    },
    [navigateToSettings, navigate, source]
  );

  const addPopoutActionListener = useCallback(() => {
    // Only add listener if we're in the main window (not the popout)
    if (source === 'popout-window') {
      return;
    }

    const handler = (event: MessageEvent) => {
      // Validate message origin for security
      if (event.origin !== window.origin) {
        return;
      }

      // Validate message structure
      if (!event.data || typeof event.data !== 'object') {
        return;
      }

      const { type, action } = event.data as { type: string; action: ChatbotAction };

      if (type !== COPILOT_ACTION_COMM_CHANNEL || !action) {
        return;
      }

      // Handle action from popout window
      handleAction(action);
    };

    messageHandlerRef.current = handler;
    window.addEventListener('message', handler);
  }, [handleAction, source]);

  const removePopoutActionListener = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
  }, []);

  useEffect(() => {
    addPopoutActionListener();

    return () => {
      removePopoutActionListener();
    };
  }, [source, addPopoutActionListener, removePopoutActionListener]);

  return {
    handleAction,
    addPopoutActionListener,
    removePopoutActionListener,
  };
};
